// 11 §3.1 `LocalMap` (decision C2), 2026-10-23 mockup pass: the adventure's site graph as nodes on a
// pixel board in the location's kit (`map/localmap.ts`; the overworld's family). Sites sit at the
// forge's (x, y) hints; edges are dotted roads (locked ones dim with a padlock until the room that
// holds the key is cleared); the party's heroes stand on `current_site` and walk the edge when the
// party travels; visited sites dim; open neighbours light up and the lead clicks one →
// `travel_to_site`. Barless (16 §2): its own top bar (adventure plate, nav, party, state, menu),
// the chat bottom left, the site card + clues + Chronicle down the right, the dialogue and open
// paths along the bottom. Layout classes `fk-localmap__*`/`fk-nodemap*` live in adventure.css.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { EmoteAnchor } from '../emote/EmoteBubble';
import { AlmanacChip, Badge, Button, ButtonRow, EmptyState, Feed, HeroSprite, OryxIcon, Panel, Rune, Skeleton, confirmDialog, useSettled, DialogueBox } from '../components';
import { useRoute } from '../../core/stores/route';
import { useSession } from '../../core/stores/session';
import { useInstance, useInstanceChronicle, useNames, useParty, usePartyMembers, useWorldClock, useWorldHeroes } from '../../core/subscriptions/hooks';
import { getBridge } from '../../core/encounter/session';
import { rpc, useToasts } from '../../core/net/reducers';
import { identityHex, tsToMs } from '../../core/net/connection';
import { CLASS_NAMES } from '../../core/sim/adapt';
import { beatText, edgeOpen, INST, INST_NAMES, openNeighbours, SITE_KIND, siteById, type Adventure, type Site, type SiteKind } from '../../core/adventure/graph';
import { ChatDock, playerColour, usePlayerColours } from './ChatDock';
import { ShellMenuButton, ShellName, ShellNav } from './Shell';
import { MapCanvas } from './map/MapCanvas';
import { RoadDots } from './map/RoadDots';
import { generateLocal, localRoute, LM_H, LM_W, type LocalBoard } from './map/localmap';
import { pathLength, pointAlong, WALK_CELLS_PER_S } from './map/overworld';
import { preloadEncounterSfx } from '../../core/audio';
import { usePlace } from '../../core/audio/places';
import { QuestPathScreen, walksAPath } from './path/QuestPath';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const SMALL_WORDS = new Set(['of', 'the', 'a', 'an', 'and', 'to', 'in', 'on', 'at', 'by']);
const titled = (s: string) => s.split(' ').map((w, i) => (i > 0 && SMALL_WORDS.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
const clock = (ms: number | undefined) => new Date(ms ?? 0).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
/** Oryx icon per site kind (the node's framed square). */
const SITE_ICON: Record<SiteKind, string> = { Gate: 'gate_open', Explore: 'world_0453', Clue: 'item_scroll', Challenge: 'item_key_gold', Escalation: 'item_sword', Sanctum: 'chest_gold_closed', Stop: 'item_sword', Boss: 'world_0556' };

/** Decode the instance blob once per instance (the blob is fixed at accept). */
function useAdventure(blob: Uint8Array | undefined): Adventure | undefined {
  return useMemo(() => {
    if (!blob || !blob.length) return undefined;
    try { return getBridge()?.decodeAdventure(blob); } catch (err) { console.error('[localmap] decode_adventure failed', err); return undefined; }
  }, [blob]);
}

/** The storyteller's line on arriving at a site (05 §6.4 beats), voiced by the site. */
function arrivalLine(s: Site, cleared: readonly number[], open: number): string {
  const ways = open === 0 ? 'No way onward is open yet.' : open === 1 ? 'One way lies open.' : `${open} ways lie open.`;
  switch (s.beat) {
    case 'Briefing': return `The road gives out at ${cap(s.name)}. Packs are checked, boots re-laced. ${ways}`;
    case 'Explore': return `${cap(s.name)}. The party fans out, eyes on the ground and the treeline. ${ways}`;
    case 'Clue': return `${cap(s.name)}. Something here was meant to stay hidden — the party marks down what it finds.`;
    // An uncleared room seen from the map means the party came back out of it (arriving opens the room).
    case 'Puzzle': return cleared.includes(s.id) ? `${cap(s.name)} stands quiet now, its mechanism spent. ${ways}` : `The party falls back from ${cap(s.name)}. Its mechanism still waits, patient as stone. ${ways}`;
    case 'Fight': return cleared.includes(s.id) ? `${cap(s.name)}. The dust has settled; the way is held. ${ways}` : `The party falls back from ${cap(s.name)} to catch its breath. The fight is not done. ${ways}`;
    case 'Settlement': return `${cap(s.name)}. The heart of it. Whatever happens here settles the matter.`;
    // docs/113: a path's stops have their own screen
    default: return `${cap(s.name)}. ${ways}`;
  }
}

/** A point halfway along a polyline (where a locked edge shows its padlock). */
function midpoint(path: ReadonlyArray<readonly [number, number]>): [number, number] {
  const seg = path.slice(1).map((p, i) => Math.hypot(p[0] - path[i]![0], p[1] - path[i]![1]));
  let half = seg.reduce((s, l) => s + l, 0) / 2;
  for (let i = 0; i < seg.length; i++) {
    if (half <= seg[i]!) { const [ax, ay] = path[i]!, [bx, by] = path[i + 1]!; const t = half / (seg[i]! || 1); return [ax + (bx - ax) * t, ay + (by - ay) * t]; }
    half -= seg[i]!;
  }
  return [path[0]![0], path[0]![1]];
}

type HeroRow = ReturnType<typeof useWorldHeroes>[number];

export function NodeMap({ a, seed, current, visited, cleared, open, canTravel, busy, onTravel, selected, onSelect, party, colours }: {
  a: Adventure; seed: number; current: number; visited: readonly number[]; cleared: readonly number[]; open: readonly number[];
  canTravel: boolean; busy: boolean; onTravel: (id: number) => void; selected?: number; onSelect: (id: number) => void;
  party: readonly HeroRow[]; colours: Map<string, number>;
}) {
  const g = a.siteGraph;
  const site = (id: number) => siteById(g, id);
  const board = useMemo(() => generateLocal(seed, a.location, g.sites, g.edges), [seed, a.location, g]);
  const title = (s: Site) => `${cap(s.name)} — ${SITE_KIND[s.kind].label}, ${beatText(s, cleared, a.battle)}${s.minutes ? `, about ${s.minutes} min` : ''}`;
  const here = board.nodes.has(current);
  return (
    <div className="fk-nodemap" role="group" aria-label={`Local map of ${a.location}`} data-testid="node-map" data-current-site={current} data-kit={board.kit} style={{ ['--ow-w' as string]: LM_W, ['--ow-h' as string]: LM_H } as CSSProperties}>
      <MapCanvas rows={board.rows} seed={seed} kit={board.kit} props={board.props} scale={2} animate />
      <svg className="fk-roads" viewBox={`0 0 ${LM_W * 24} ${LM_H * 24}`} aria-hidden="true">
        {g.edges.map((e, i) => {
          const path = board.paths[i];
          if (!path || path.length < 2) return null;
          const isOpen = edgeOpen(e, cleared);
          const onPath = (e.a === current && open.includes(e.b)) || (e.b === current && open.includes(e.a));
          const walked = visited.includes(e.a) && visited.includes(e.b);
          const [mx, my] = midpoint(path);
          return (
            <RoadDots key={i} path={path} skip={14} className={`fk-nodemap__edge ${isOpen ? '' : 'fk-roads__road--dim fk-nodemap__edge--locked'} ${onPath ? 'fk-roads__road--lit fk-nodemap__edge--open' : ''} ${walked ? 'fk-nodemap__edge--walked' : ''}`} data-testid="map-edge" data-locked={!isOpen}>
              {!isOpen && (
                <g transform={`translate(${mx * 24} ${my * 24})`} className="fk-nodemap__lock" aria-label={`locked until ${site(e.lockedBy ?? -1)?.name ?? 'the room'} is cleared`}>
                  <rect x="-9" y="-9" width="18" height="18" className="fk-nodemap__lock-plate" />
                  <path d="M -3.5 -1 v -3 a 3.5 3.5 0 0 1 7 0 v 3" className="fk-nodemap__lock-shackle" />
                  <rect x="-5" y="-1" width="10" height="7" className="fk-nodemap__lock-body" />
                </g>
              )}
            </RoadDots>
          );
        })}
      </svg>
      {g.sites.map((s) => {
        const n = board.nodes.get(s.id);
        if (!n) return null;
        const isCurrent = s.id === current;
        const isOpen = open.includes(s.id) && !isCurrent;
        const isVisited = visited.includes(s.id) && !isCurrent;
        const isCleared = cleared.includes(s.id);
        const clickable = isOpen && canTravel && !busy;
        const cls = ['fk-nodemap__site', `fk-nodemap__site--${s.kind.toLowerCase()}`, isCurrent ? 'fk-nodemap__site--current' : '', isOpen ? 'fk-nodemap__site--open' : '', isVisited ? 'fk-nodemap__site--visited' : '', clickable ? 'fk-nodemap__site--clickable' : '', s.id === selected ? 'fk-nodemap__site--selected' : ''].filter(Boolean).join(' ');
        const act = () => { onSelect(s.id); if (clickable) onTravel(s.id); };
        return (
          <div key={s.id} className={cls} style={{ ['--x' as string]: n.x, ['--y' as string]: n.y } as CSSProperties}>
            {/* one hit target from the icon to its label (16 §1: a click between them still lands on the site) */}
            <div className="fk-nodemap__hit fk-hit" role="button" tabIndex={0} aria-label={title(s)} aria-disabled={!clickable} title={title(s)}
              data-testid="site-node" data-site-id={s.id} data-site-kind={s.kind} data-open={isOpen} data-current={isCurrent} data-visited={isVisited} data-cleared={isCleared}
              onClick={act} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); act(); } }}>
              <span className="fk-nodemap__icon">
                <OryxIcon name={SITE_ICON[s.kind]} scale={2} glyph={SITE_KIND[s.kind].glyph} />
                {isCleared && <span className="fk-nodemap__check" aria-label="cleared">✓</span>}
              </span>
              <span className="fk-nodemap__plate">{titled(s.name)}</span>
              <span className="fk-nodemap__sub">{SITE_KIND[s.kind].label}{s.minutes ? ` · ${s.minutes} min` : ''}</span>
            </div>
          </div>
        );
      })}
      {here && <PartyMarker board={board} edges={g.edges} current={current} party={party} colours={colours} />}
    </div>
  );
}

/**
 * The party on the local map: it stands over `current_site`, and when the party travels it walks
 * the edge's road at the overworld's pace (docs/36), turning at the elbow, instead of cutting across.
 */
function PartyMarker({ board, edges, current, party, colours }: {
  board: LocalBoard; edges: Adventure['siteGraph']['edges']; current: number; party: readonly HeroRow[]; colours: Map<string, number>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const was = useRef(current);
  const me = useSession((s) => s.identity ?? '');
  useLayoutEffect(() => {
    const el = ref.current;
    const rest = board.nodes.get(current);
    if (!el || !rest) return;
    const put = (x: number, y: number, dx: number, moving: boolean) => {
      el.style.setProperty('--x', String(x));
      el.style.setProperty('--y', String(y));
      if (dx) el.dataset.face = dx < 0 ? 'left' : 'right';
      el.dataset.walking = moving ? 'true' : 'false';
    };
    const route = was.current !== current ? localRoute(board, edges, was.current, current) : undefined;
    was.current = current;
    if (!route) { put(rest.x, rest.y, 0, false); return; }
    const ms = (pathLength(route) / WALK_CELLS_PER_S) * 1000;
    const start = performance.now();
    let raf = 0;
    const tick = () => {
      const t = performance.now() - start;
      if (t >= ms) { put(rest.x, rest.y, 0, false); return; }
      const p = pointAlong(route, (t / 1000) * WALK_CELLS_PER_S);
      put(p.x, p.y, p.dx, true);
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [board, edges, current]);
  return (
    <div ref={ref} className="fk-nodemap__party" data-testid="map-party" data-site={current} aria-hidden="true">
      {party.slice(0, 4).map((h) => (
        <span key={h.id.toString()} className="fk-nodemap__token" style={{ ['--fk-seat' as string]: playerColour(colours.get(identityHex(h.owner))) } as CSSProperties}>
          <span className="fk-nodemap__token-tag">{h.name}</span>
          <span className="fk-nodemap__token-body"><HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={CLASS_NAMES[h.class] ?? 'fighter'} scale={2} title={h.name} /></span>
          <EmoteAnchor heroes={[{ id: h.id, name: h.name, mine: identityHex(h.owner) === me }]} />
        </span>
      ))}
      {party.length > 4 && <span className="fk-nodemap__token-more">+{party.length - 4}<EmoteAnchor heroes={party.slice(4).map((h) => ({ id: h.id, name: h.name, mine: identityHex(h.owner) === me }))} /></span>}
    </div>
  );
}

export function LocalMapScreen() {
  // docs/34: fetch the fight's sounds while the party gathers, so the foe's first growl is ready
  useEffect(() => { preloadEncounterSfx(); }, []);
  usePlace({ key: 'place.camp' });   // docs/44: the open country between an adventure's sites
  const worldId = useSession((s) => s.worldId);
  const me = useSession((s) => s.identity ?? '');
  const instanceId = useRoute((s) => s.instanceId);
  const partyId = useRoute((s) => s.partyId);
  const go = useRoute((s) => s.go);
  const inst = useInstance(instanceId);
  const party = useParty(partyId ?? inst?.partyId);
  const members = usePartyMembers(partyId ?? inst?.partyId);
  const heroes = useWorldHeroes(worldId);
  const names = useNames(worldId);
  const colours = usePlayerColours(worldId);
  const chronicle = useInstanceChronicle(worldId, instanceId);
  const a = useAdventure(inst?.blob);
  const settled = useSettled(1500, instanceId);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<number | undefined>();
  /** Which dialogue line has been read at which stop (`instance:site`); a new stop starts at line 0. */
  const [read, setRead] = useState<{ key: string; i: number }>({ key: '', i: 0 });
  const push = useToasts((s) => s.push);
  const bar = (plate: ReactNode, aside?: ReactNode) => (
    <header className="fk-localmap__bar">
      <div className="fk-localmap__lead">
        <div className="fk-localmap__plate fk-hit">{plate}</div>
        <div className="fk-localmap__navrow fk-hit"><ShellNav /><ShellName label={false} /></div>
      </div>
      <span className="fk-localmap__spacer" />
      <div className="fk-localmap__aside fk-hit">{aside}<ShellMenuButton /></div>
    </header>
  );

  if (!inst || !a) {
    // The instance row may still be arriving, or the blob still decoding (U5): rune, then an honest empty state.
    const waiting = !!inst || !settled;
    return (
      <main className="fk-localmap fk-localmap--loading" data-testid="localmap-screen">
        {bar(<><span className="fk-localmap__eyebrow">Local map</span>{waiting ? <Skeleton kind="title" width="14rem" /> : <h2 className="fk-localmap__title">No adventure under way</h2>}</>)}
        <Panel variant="solid" className="fk-localmap__wait" label="Local map">
          {waiting
            ? <div className="fk-localmap__unroll" role="status" aria-label="Unrolling the map"><Rune /><span className="fk-localmap__beat">Unrolling the map…</span></div>
            : <EmptyState glyph="⌂" title="The map is rolled up" action={<Button primary onClick={() => go('world')}>Back to the World</Button>}>Accept an offer from the World's board to set out on an adventure.</EmptyState>}
        </Panel>
      </main>
    );
  }
  // docs/113: a quest walked as a path of stops has its own screen
  if (walksAPath(a)) return <QuestPathScreen inst={inst} a={a} />;
  const g = a.siteGraph;
  const isLead = party ? identityHex(party.lead) === me : false;
  const leadName = party ? names[identityHex(party.lead)] ?? 'the lead' : 'the lead';
  const open = inst.state === INST.LOADED ? openNeighbours(g, inst.currentSite, inst.cleared) : [];
  const here = siteById(g, inst.currentSite);
  const clues = chronicle.filter((c) => c.kindName === 'SecretLearned');
  const heroById = new Map(heroes.map((h) => [h.id.toString(), h]));
  const shown = siteById(g, selected ?? inst.currentSite);
  const inRoom = inst.state === INST.IN_ENCOUNTER;
  const pitch = a.pitch.replace('{asker}', a.asker);
  const seed = Math.imul(Number(inst.id % 2147483647n) + 1, 2654435761) >>> 0;
  const travel = async (id: number) => {
    if (!isLead) { push(`Waiting for ${leadName} to move the party.`); return; }
    setBusy(true);
    await rpc.travelToSite(inst.id, id);
    setBusy(false);
  };
  const abandon = async () => {
    const ok = await confirmDialog({ eyebrow: inst.title, title: 'Abandon the adventure?', text: 'The Chronicle records it; the party keeps a token of XP.', confirmLabel: 'Abandon', cancelLabel: 'Press on', danger: true });
    if (ok) void rpc.abandonAdventure(inst.id);
  };

  // Dialogue (11 §10.10 DialogueBox): on arrival at a site, the site speaks its beat; on the very first
  // stop the asker's pitch comes first. Click advances / dismisses; nothing waits on it.
  const stop = `${inst.id}:${inst.currentSite}`;
  const atStart = inst.currentSite === g.start && inst.visited.length <= 1;
  const lines: Array<{ speaker: string; text: string; portrait: ReactNode }> = [
    ...(atStart ? [{ speaker: a.asker, text: pitch, portrait: <OryxIcon name="item_scroll" scale={3} /> }] : []),
    ...(here && !inRoom ? [{ speaker: cap(here.name), text: arrivalLine(here, inst.cleared, open.length), portrait: <OryxIcon name={SITE_ICON[here.kind]} scale={3} glyph={SITE_KIND[here.kind].glyph} /> }] : []),
  ];
  const lineIx = read.key === stop ? read.i : 0;
  const line = lines[lineIx];
  const advance = () => setRead({ key: stop, i: lineIx + 1 });

  const deployed = [...members].sort((x, y) => (x.id < y.id ? -1 : 1));
  const partyHeroes = deployed.filter((m) => m.deployed).map((m) => heroById.get(m.heroId.toString())).filter((h): h is HeroRow => !!h);
  const clueItems = clues.map((c) => ({ key: c.id.toString(), time: clock(tsToMs(c.at)), text: <span data-testid="clue-line">{c.summary}</span>, glyph: '?', glyphTitle: 'clue' }));
  const chronItems = [...chronicle].reverse().map((c) => ({ key: c.id.toString(), time: clock(tsToMs(c.at)), text: c.summary, glyph: '◆', glyphTitle: c.kindName }));
  const canAbandon = isLead && inst.state <= INST.IN_ENCOUNTER;

  return (
    <main className="fk-localmap" data-testid="localmap-screen" data-instance-id={inst.id.toString()} data-instance-state={inst.state} data-current-site={inst.currentSite}>
      <div className="fk-localmap__map">
        <div className="fk-localmap__stage">
          <NodeMap a={a} seed={seed} current={inst.currentSite} visited={inst.visited} cleared={inst.cleared} open={open} canTravel={isLead && !inRoom} busy={busy} onTravel={(id) => void travel(id)} selected={selected} onSelect={setSelected} party={partyHeroes} colours={colours} />
        </div>
      </div>

      {bar(
        <>
          <span className="fk-localmap__eyebrow">{a.archetype} · {titled(inst.location)}</span>
          <LocalDate worldId={inst.worldId} />
          <h2 className="fk-localmap__title" data-testid="adventure-title">{inst.title}</h2>
          <span className="fk-localmap__stats">Asked by {a.asker} · lead {leadName}{isLead ? ' (you)' : ''}</span>
        </>,
        <>
          <span className="fk-localmap__chips" aria-label="The party">
            {deployed.map((m) => { const h = heroById.get(m.heroId.toString()); const owner = identityHex(m.owner); return (
              <span key={m.id.toString()} className={`fk-localmap__chip ${m.deployed ? '' : 'fk-localmap__chip--reserve'}`} style={{ ['--fk-seat' as string]: playerColour(colours.get(owner)) } as CSSProperties} title={`${h?.name ?? `hero ${m.heroId}`} — L${h?.level ?? 1} ${cap(CLASS_NAMES[h?.class ?? 0] ?? '')} · ${names[owner] ?? '…'}${m.deployed ? '' : ' · reserve'}`}>
                <HeroSprite heroId={h?.id} species={h?.species} frame={h?.oryxFrame} classId={CLASS_NAMES[h?.class ?? 0]} scale={2} title={h?.name} />
                <span className="fk-localmap__chip-name">{h?.name ?? '…'}</span>
              </span>
            ); })}
          </span>
          <span className={`fk-localmap__state ${inRoom ? 'fk-localmap__state--room' : ''}`}>{INST_NAMES[inst.state]}</span>
          <span className="fk-localmap__par" title="the forge's par time">par {a.parMinutes} min</span>
          {canAbandon && <Button variant="danger" small disabled={inRoom} title={inRoom ? 'finish or abandon the room first' : 'Abandon the adventure'} onClick={() => void abandon()} data-testid="abandon-adventure">Abandon</Button>}
        </>,
      )}

      {worldId && <div className="fk-localmap__chat"><ChatDock worldId={worldId} partyId={inst.partyId} variant="panel" className="fk-localmap__chatdock" /></div>}

      <div className="fk-localmap__foot">
        {line && <DialogueBox key={`${stop}:${lineIx}`} className="fk-localmap__dialogue" speaker={line.speaker} text={line.text} portrait={line.portrait} more={lineIx < lines.length - 1} onAdvance={advance} data-testid="map-dialogue" />}
        <div className="fk-localmap__paths fk-hit">
          {inRoom && inst.encounterId ? (
            <><span className="fk-localmap__label">A room is open</span><Button primary small onClick={() => go('encounter', { encounterId: inst.encounterId })} data-testid="rejoin-room">Rejoin the room</Button></>
          ) : (
            <>
              <span className="fk-localmap__label">Open paths</span>
              <ButtonRow data-testid="open-paths">
                {open.length === 0 && <span className="fk-localmap__none">no open path from here</span>}
                {open.map((id) => { const s = siteById(g, id); return s ? (
                  <Button key={id} small primary={!inst.visited.includes(id)} icon={SITE_KIND[s.kind].glyph} disabled={!isLead || busy} title={isLead ? beatText(s, inst.cleared) : `waiting for ${leadName}`} onClick={() => void travel(id)} data-testid="travel-button" data-site-id={id}>{cap(s.name)}</Button>
                ) : null; })}
              </ButtonRow>
            </>
          )}
          {!isLead && !inRoom && <span className="fk-localmap__hint" data-testid="waiting-for-lead">Waiting for {leadName} to move the party.</span>}
        </div>
      </div>

      <aside className="fk-localmap__right">
        <Panel variant="solid" className="fk-localmap__card" label="Site">
          {shown && (
            <>
              <div className="fk-localmap__card-top">
                <span className={`fk-localmap__tag ${shown.id === inst.currentSite ? 'fk-localmap__tag--here' : ''}`}>{shown.id === inst.currentSite ? 'The party is here' : inst.visited.includes(shown.id) ? 'Visited' : open.includes(shown.id) ? 'Open from here' : 'Not yet visited'}</span>
                <span className="fk-localmap__kind">{SITE_KIND[shown.kind].label}{shown.minutes > 0 ? ` · about ${shown.minutes} min` : ''}</span>
              </div>
              <h3 className="fk-localmap__site-name" data-testid="current-site">{titled(shown.name)}</h3>
              <p className="fk-localmap__beat">{cap(beatText(shown, inst.cleared, a?.battle))}.</p>
              <div className="fk-localmap__marks">
                {inst.cleared.includes(shown.id) && <Badge tone="green">cleared</Badge>}
                {inRoom ? <Badge tone="amber" pulse>a room is open</Badge> : shown.id === inst.currentSite && <Badge tone={open.length ? 'green' : 'grey'}>{open.length ? `${open.length} way${open.length === 1 ? '' : 's'} open` : 'no way open'}</Badge>}
              </div>
              <dl className="fk-localmap__tally">
                <dt>Visited</dt><dd>{inst.visited.length} / {g.sites.length}</dd>
                <dt>Cleared</dt><dd>{inst.cleared.length}</dd>
                <dt>Wipes</dt><dd>{inst.wipes}</dd>
              </dl>
              {open.includes(shown.id) && isLead && !inRoom && <Button primary block disabled={busy} onClick={() => void travel(shown.id)} data-testid="travel-selected">Travel to {titled(shown.name)}</Button>}
            </>
          )}
        </Panel>
        <Panel variant="solid" className="fk-localmap__log" label="Clues and the Chronicle">
          <div className="fk-localmap__section"><span className="fk-localmap__label">Clues</span><span className="fk-localmap__count">{inst.cluesFound.length}</span></div>
          {clueItems.length === 0
            ? <p className="fk-localmap__none" data-testid="clues">Clue sites record what the party finds.</p>
            : <Feed items={clueItems} data-testid="clues" className="fk-localmap__feed" />}
          <div className="fk-localmap__section"><span className="fk-localmap__label">Chronicle</span></div>
          {chronItems.length === 0
            ? <p className="fk-localmap__none">The Chronicle writes as the party moves.</p>
            : <Feed items={chronItems} data-testid="instance-chronicle" className="fk-localmap__feed fk-localmap__feed--chron" />}
        </Panel>
      </aside>
    </main>
  );
}

/** The World's date over the adventure (docs/23 Y8). */
function LocalDate({ worldId }: { worldId: bigint }) {
  const clock = useWorldClock(worldId);
  return clock ? <AlmanacChip clock={clock} className="fk-localmap__date" /> : null;
}
